'use client';

import { useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

/**
 * Deterministic tick clock for the mechanism widgets. Starts when the element
 * first scrolls into view, pauses while off screen (no interval fires for a
 * hidden widget), and under reduced motion jumps straight to the final tick.
 * Everything a widget draws must be a pure function of `tick`.
 *
 * Hydration invariant: motion's useReducedMotion() returns null on the server
 * but the real boolean on the client's first render, so reading it directly
 * would desync server and client markup for reduced-motion visitors. The
 * `mounted` gate keeps the first client render identical to the server (both
 * see reduceMotion=false, tick=0); the preference applies one commit later.
 */
export function useSim({
  totalTicks,
  msPerTick,
  loop = false,
}: {
  totalTicks: number;
  msPerTick: number;
  loop?: boolean;
}) {
  const prefersReduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const [tick, setTick] = useState(0);
  const [started, setStarted] = useState(false);
  const [inView, setInView] = useState(false);
  const [runId, setRunId] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const reduceMotion = mounted && !!prefersReduced;

  // The observer stays connected: `started` latches the first reveal so a
  // finished one-shot sim keeps its end state, while `inView` gates the
  // interval so loops pause off screen or inside a collapsed panel.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runId is a deliberate re-arm signal for replay(), not a read dependency
  useEffect(() => {
    if (!started) return;
    if (reduceMotion) {
      setTick(totalTicks);
      return;
    }
    if (!inView) return;
    const id = setInterval(() => {
      setTick((t) => {
        if (t < totalTicks) return t + 1;
        if (loop) return 0;
        clearInterval(id);
        return t;
      });
    }, msPerTick);
    return () => clearInterval(id);
  }, [started, inView, reduceMotion, totalTicks, msPerTick, loop, runId]);

  // Restart from zero and re-arm the interval even if a one-shot sim already
  // cleared it (the runId bump re-runs the interval effect).
  const replay = () => {
    setTick(0);
    setRunId((r) => r + 1);
  };

  const done = tick >= totalTicks;

  return { ref, tick, replay, done, reduceMotion };
}
